/**
 * Lead Processing Service
 *
 * API service for post-processor-selection workflow:
 * - Processor declining, creating/submitting bank applications
 * - Recording bank responses (approved/rejected/declined/withdrawn)
 * - Agent decisions after rejection (retry/reopen/close)
 * - Processing history and offer statuses
 */

import axiosInstance from '../config/axios.config';

// ================================
// STATUS CONSTANTS
// ================================

export const PROCESSING_STATUS = {
    // New unified statuses (15 values — single source of truth)
    new_lead:            { label: 'New Lead',            severity: 'info',      icon: 'pi-star' },
    offers_generated:    { label: 'Offers Generated',    severity: 'success',   icon: 'pi-check' },
    broadcasted:         { label: 'Broadcasted',         severity: 'info',      icon: 'pi-megaphone' },
    processing:          { label: 'Processing',          severity: 'warning',   icon: 'pi-spin pi-spinner' },
    bank_submit:         { label: 'Bank Submit',         severity: 'info',      icon: 'pi-building' },
    bank_queries:        { label: 'Bank Queries',        severity: 'warning',   icon: 'pi-building' },
    bank_processing:     { label: 'Bank Processing',     severity: 'info',      icon: 'pi-cog' },
    sanction:            { label: 'Sanction',            severity: 'success',   icon: 'pi-check-circle' },
    sanction_queries:    { label: 'Sanction Queries',    severity: 'warning',   icon: 'pi-building' },
    disbursal_queries:   { label: 'Disbursal Queries',   severity: 'warning',   icon: 'pi-building' },
    disbursal:           { label: 'Disbursal',           severity: 'success',   icon: 'pi-wallet' },
    bank_rejected:       { label: 'Bank Rejected',       severity: 'danger',    icon: 'pi-times-circle' },
    customer_rejected:   { label: 'Customer Rejected',   severity: 'danger',    icon: 'pi-user-minus' },
    processor_rejected:  { label: 'Processor Rejected',  severity: 'danger',    icon: 'pi-exclamation-triangle' },
    closed:              { label: 'Closed',              severity: 'secondary', icon: 'pi-lock' },
    // Legacy aliases (old data in DB — will be migrated)
    not_started:         { label: 'Not Started',         severity: 'secondary', icon: 'pi-circle' },
    processor_selected:  { label: 'Processing',          severity: 'warning',   icon: 'pi-spin pi-spinner' },
    in_processing:       { label: 'Processing',          severity: 'warning',   icon: 'pi-spin pi-spinner' },
    in_progress:         { label: 'Processing',          severity: 'warning',   icon: 'pi-spin pi-spinner' },
    submitted_to_bank:   { label: 'Bank Submit',         severity: 'info',      icon: 'pi-building' },
    submitted:           { label: 'Bank Submit',         severity: 'info',      icon: 'pi-building' },
    approved:            { label: 'Sanction',            severity: 'success',   icon: 'pi-check-circle' },
    rejected:            { label: 'Rejected',            severity: 'danger',    icon: 'pi-times-circle' },
    disbursed:           { label: 'Disbursal',           severity: 'success',   icon: 'pi-wallet' },
    exhausted:           { label: 'Closed',              severity: 'secondary', icon: 'pi-lock' },
    retry_needed:        { label: 'Bank Rejected',       severity: 'danger',    icon: 'pi-times-circle' },
    customer_declined:   { label: 'Customer Rejected',   severity: 'danger',    icon: 'pi-user-minus' },
};

export const OFFER_STATUS = {
    available:          { label: 'Available',          severity: 'success',   icon: 'pi-check' },
    processing:         { label: 'Processing',         severity: 'info',      icon: 'pi-spin pi-spinner' },
    bank_submit:        { label: 'Bank Submit',        severity: 'info',      icon: 'pi-building' },
    bank_rejected:      { label: 'Bank Rejected',      severity: 'danger',    icon: 'pi-times-circle' },
    customer_rejected:  { label: 'Customer Rejected',  severity: 'danger',    icon: 'pi-user-minus' },
    processor_rejected: { label: 'Processor Rejected', severity: 'danger',    icon: 'pi-exclamation-triangle' },
    sanction:           { label: 'Sanctioned',         severity: 'success',   icon: 'pi-check-circle' },
    disbursal:          { label: 'Disbursed',          severity: 'success',   icon: 'pi-wallet' },
    // Legacy aliases
    in_processing: { label: 'Processing', severity: 'info',      icon: 'pi-spin pi-spinner' },
    rejected:      { label: 'Rejected',   severity: 'danger',    icon: 'pi-lock' },
    approved:      { label: 'Sanctioned', severity: 'success',   icon: 'pi-check-circle' },
    disbursed:     { label: 'Disbursed',  severity: 'success',   icon: 'pi-check-circle' },
};

export const REJECTION_REASONS = [
    { code: 'low_cibil',                label: 'CIBIL Score Too Low' },
    { code: 'missed_payments',          label: 'Payment History Issue' },
    { code: 'high_foir',               label: 'High Fixed Obligation Ratio' },
    { code: 'insufficient_income',      label: 'Insufficient Income' },
    { code: 'property_valuation',       label: 'Property Valuation Issue' },
    { code: 'employment_type',          label: 'Employment Type Not Accepted' },
    { code: 'age_limit',               label: 'Age Not Within Criteria' },
    { code: 'documentation_incomplete', label: 'Incomplete Documents' },
    { code: 'location_not_serviceable', label: 'Location Not Serviceable' },
    { code: 'loan_amount',             label: 'Loan Amount Not Eligible' },
    { code: 'existing_loan_burden',     label: 'Existing Loan Obligations' },
    { code: 'company_not_approved',     label: 'Employer Not in Approved List' },
    { code: 'other',                    label: 'Other' },
];

export const PROCESSOR_REJECTION_REASONS = [
    { code: 'bank_criteria_mismatch',     label: 'Customer Does Not Meet Bank Criteria' },
    { code: 'documentation_not_possible', label: 'Required Documents Not Available' },
    { code: 'bank_not_serviceable',       label: 'Bank Cannot Service This Location' },
    { code: 'product_not_suitable',       label: 'Loan Product Not Suitable' },
    { code: 'rate_not_competitive',       label: 'Rate/Terms Not Competitive' },
    { code: 'customer_profile_issue',     label: 'Customer Profile Issue' },
    { code: 'other',                      label: 'Other' },
];

export const getReasonLabel = (code) =>
    REJECTION_REASONS.find(r => r.code === code)?.label
    || PROCESSOR_REJECTION_REASONS.find(r => r.code === code)?.label
    || code;

export const getProcessingStatusConfig = (status) =>
    PROCESSING_STATUS[status] || { label: status ? status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Unknown', severity: 'secondary', icon: 'pi-circle' };

export const getOfferStatusConfig = (status) =>
    OFFER_STATUS[status] || { label: status ? status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Unknown', severity: 'secondary', icon: 'pi-circle' };

// ================================
// API SERVICE
// ================================

export const declineAssignment = async (broadcastId, reason) => {
    try {
        const response = await axiosInstance.post(`/broadcasts/${broadcastId}/decline-assignment`, { reason });
        return response.data;
    } catch (error) {
        console.error('leadProcessingService.declineAssignment:', error);
        throw error.response?.data || error;
    }
};

export const createApplication = async (broadcastId, offerId) => {
    try {
        const response = await axiosInstance.post(`/broadcasts/${broadcastId}/processing-applications`, { offer_id: offerId });
        return response.data;
    } catch (error) {
        console.error('leadProcessingService.createApplication:', error);
        throw error.response?.data || error;
    }
};

export const submitApplication = async (applicationId, data = {}) => {
    try {
        const response = await axiosInstance.patch(`/processing-applications/${applicationId}/submit`, data);
        return response.data;
    } catch (error) {
        console.error('leadProcessingService.submitApplication:', error);
        throw error.response?.data || error;
    }
};

export const recordResult = async (applicationId, data) => {
    try {
        const response = await axiosInstance.patch(`/processing-applications/${applicationId}/result`, data);
        return response.data;
    } catch (error) {
        console.error('leadProcessingService.recordResult:', error);
        throw error.response?.data || error;
    }
};

export const processorRejectApplication = async (applicationId, data) => {
    try {
        const response = await axiosInstance.patch(`/processing-applications/${applicationId}/processor-reject`, data);
        return response.data;
    } catch (error) {
        console.error('leadProcessingService.processorRejectApplication:', error);
        throw error.response?.data || error;
    }
};

export const recordDisbursement = async (applicationId, data = {}) => {
    try {
        const response = await axiosInstance.patch(`/processing-applications/${applicationId}/disburse`, data);
        return response.data;
    } catch (error) {
        console.error('leadProcessingService.recordDisbursement:', error);
        throw error.response?.data || error;
    }
};

export const processingDecision = async (broadcastId, data) => {
    try {
        const response = await axiosInstance.post(`/broadcasts/${broadcastId}/processing-decision`, data);
        return response.data;
    } catch (error) {
        console.error('leadProcessingService.processingDecision:', error);
        throw error.response?.data || error;
    }
};

export const getProcessingHistory = async (broadcastId) => {
    try {
        const response = await axiosInstance.get(`/broadcasts/${broadcastId}/processing-history`);
        return response.data;
    } catch (error) {
        console.error('leadProcessingService.getProcessingHistory:', error);
        throw error.response?.data || error;
    }
};

export const getOfferStatuses = async (broadcastId) => {
    try {
        const response = await axiosInstance.get(`/broadcasts/${broadcastId}/offer-statuses`);
        return response.data;
    } catch (error) {
        console.error('leadProcessingService.getOfferStatuses:', error);
        throw error.response?.data || error;
    }
};
